import { inject, injectable } from 'inversify';
import 'reflect-metadata';

import { Unit, convertToPixel, removeTrailingSlash } from '@desygner/ui-common-svg-utils';
import { t } from '@desygner/ui-common-translation';
import {
  TYPES as ApiGatewayTypes,
  type IApiGateway,
  isErrorResponse,
} from '@desygner/ui-core-api-gateway';
import { createAureliaLoaderConfigModule } from '@desygner/ui-core-aurelia-loader';
import {
  TYPES as DATA_ACCESS_TYPES,
  ICollaborateService,
} from '@desygner/ui-data-access-collaborate';
import { IUserService, TYPES as USER_SERVICE_TYPES, UserData } from '@desygner/ui-data-access-user';
import {
  type ICompanyService,
  TYPES as WORKSPACE_SERVICE_TYPES,
} from '@desygner/ui-data-access-workspace';
import {
  FontManager,
  TYPES as PROJECTS_STORES_TYPES,
  ProjectBody,
  ProjectStoreInjector,
  ProjectsFacade,
  PublicProjectBody,
} from '@desygner/ui-features-projects';

import * as config from '../../config';
import { TYPES, appContainer } from '../../inversify.config';
import {
  IConfigStoreInjector,
  IUserStoreInjector,
  IViewerStoreInjector,
  IWorkspaceStoreInjector,
  TYPES as STORE_TYPES,
} from '../../store';
import { Status } from '../../store/viewer/viewer.types';
import { ICredentialsManager } from './credentials-manager';
import { IErrorManager } from './error-manager';
import { IKeyboardShortcutsManager } from './keyboard-shortcuts-manager';
import { IRestrictionsManager } from './restrictions-manager';
import { SVGManager } from './svg-manager';
import { IWorkflowManager } from './workflow-manager';

export interface IViewerLoader {
  initialize: () => void;
  loadUserData: () => Promise<UserData | null>;
  loadViewerProject: () => Promise<ProjectBody | PublicProjectBody | void>;
}
@injectable()
export class ViewerLoader implements IViewerLoader {
  constructor(
    @inject(ApiGatewayTypes.ApiGateway)
    private readonly apiGateway: IApiGateway,

    @inject(STORE_TYPES.Config)
    private readonly configStore: IConfigStoreInjector,

    @inject(STORE_TYPES.Viewer)
    private readonly viewerStore: IViewerStoreInjector,

    @inject(STORE_TYPES.User)
    private readonly userStore: IUserStoreInjector,

    @inject(PROJECTS_STORES_TYPES.ProjectsStore)
    private readonly projectsStore: ProjectStoreInjector,

    @inject(PROJECTS_STORES_TYPES.FontManager)
    private readonly fontManager: FontManager,

    @inject(USER_SERVICE_TYPES.UserService)
    private readonly userService: IUserService,

    @inject(TYPES.Viewer.KeyboardShortcutsManager)
    private readonly keyboardShortcutsManager: IKeyboardShortcutsManager,

    @inject(TYPES.Viewer.CredentialsManager)
    private readonly credentialsManager: ICredentialsManager,

    @inject(TYPES.Viewer.ErrorManager)
    private readonly errorManager: IErrorManager,

    @inject(TYPES.Viewer.SVGManager)
    private readonly svgManager: SVGManager,

    @inject(TYPES.Viewer.RestrictionsManager)
    private readonly restrictionsManager: IRestrictionsManager,

    @inject(DATA_ACCESS_TYPES.CollaborateService)
    private collaborateService: ICollaborateService,

    @inject(TYPES.Viewer.WorkflowManager)
    private readonly workflowManager: IWorkflowManager,

    @inject(WORKSPACE_SERVICE_TYPES.CompanyService)
    private readonly companyService: ICompanyService,

    @inject(STORE_TYPES.Workspace)
    private readonly workspaceStore: IWorkspaceStoreInjector
  ) {}

  private configureApiGateway() {
    const configStore = this.configStore.getState();
    const { apiUrl, aiServerUrl, authUrl, fluerApiUrl } = configStore.api;
    const { company } = this.viewerStore.getState();

    const { token, type } = this.credentialsManager.getUserTokenData();

    const services = [
      ['ai', aiServerUrl],
      ['auth', authUrl],
      ['fluer', fluerApiUrl],
    ];

    if (!company) return;

    this.apiGateway.configure(
      apiUrl,
      {
        type,
        token,
      },
      company.domain
    );

    services.forEach(([name, apiUrl]) => {
      this.apiGateway.registerInstance(name, {
        apiUrl,
        token: {
          type,
          token,
        },
      });
    });
  }

  private listenApiGatewayUpdates() {
    const { company } = this.viewerStore.getState();
    const { userToken } = this.userStore.getState();

    this.userStore.subscribe((state) => {
      const isUserUpdated = state.userToken !== userToken;

      if (isUserUpdated) {
        this.configureApiGateway();
      }
    });

    this.viewerStore.subscribe((state) => {
      const isCompanyUpdated = state.company?.domain !== company?.domain;

      if (isCompanyUpdated) {
        this.configureApiGateway();
      }
    });
  }

  private listenTranslationUpdates() {
    const { isTranslationLoaded } = this.viewerStore.getState();
    const { project } = this.projectsStore.getState();

    const updateTranslation = () => {
      if (!this.viewerStore.getState().isTranslationLoaded) {
        return;
      }

      const name = project.currentProject?.name || t('shared.project.untitled_project');

      project.setProject({
        ...project.currentProject!,
        name,
      });
    };

    if (isTranslationLoaded) {
      updateTranslation();
      return;
    }

    this.viewerStore.subscribe((state) => {
      if (state.isTranslationLoaded && !isTranslationLoaded) {
        updateTranslation();
      }
    });
  }

  private async loadSVGSprite() {
    try {
      const response = await fetch(config.icons.url);
      const iconsXML = await response.text();
      const container = document.createElement('div');

      document.body && document.body.appendChild(container);
      container.style.display = 'none';
      container.innerHTML = iconsXML;
    } catch (error) {
      this.errorManager.handleUnknownError(error as Error);
    }
  }

  private updateFavicon(faviconUrl: string) {
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;

    if (favicon) {
      favicon.href = faviconUrl;
    }
  }

  private async loadCompanyData(companyData: PublicProjectBody['company']): Promise<void> {
    const { brand } = this.configStore.getState();
    const { setCompany } = this.viewerStore.getState();
    const { company } = this.workspaceStore.getState();
    const isWorkspace = companyData.id !== brand.companyId;
    const parentDomain = companyData.parent_companies?.[0]?.domain ?? null;

    setCompany({
      ...companyData,
      is_workspace: isWorkspace,
      parent_domain: parentDomain,
      white_label_configuration: companyData.white_label_configuration && {
        ...companyData.white_label_configuration,
        ...((isWorkspace && { domain: companyData.domain }) || {}),
      },
    });

    //? update workspace favicon
    if (isWorkspace && 'white_label_configuration' in companyData) {
      const faviconUrl = companyData.white_label_configuration.logo_url;

      if (faviconUrl) {
        this.updateFavicon(faviconUrl);
      }
    }

    const fullCompanyData = await this.companyService.fetchCompanyData();

    company.setCompanyData(fullCompanyData);
  }

  private async loadProjectData(id: string) {
    const { setStatus, project } = this.viewerStore.getState();
    const { userToken, setIsCredentialValid } = this.userStore.getState();
    const projectService = appContainer.get<ProjectsFacade>(PROJECTS_STORES_TYPES.ProjectsFacade);

    const publicProjectData = await projectService.loadPublicProject(id, project.type);
    let projectData: PublicProjectBody | ProjectBody = publicProjectData;
    const isPrivate = publicProjectData.is_public === false;

    const [, userData] = await Promise.allSettled([
      this.loadCompanyData(publicProjectData.company),
      (isPrivate || userToken) && this.loadUserData(),
    ]);

    this.updateProjectTitle();

    if (isPrivate || userToken) {
      if (userData.status === 'fulfilled' && !userData.value && isPrivate) {
        setStatus(Status.CredentialsInvalid);
        setIsCredentialValid(false);
        return;
      }

      const [, projectDataResponse] = await Promise.allSettled([
        this.loadDesignCollaborators({
          projectId: publicProjectData.id,
          encodedId: publicProjectData.encoded_id,
        }),
        projectService.loadProject(id, project.type),
      ]);

      if (projectDataResponse.status === 'fulfilled') {
        projectData = projectDataResponse.value;
      }

      this.restrictionsManager.init();
      this.workflowManager.init();
    }

    if (!projectData) {
      setStatus(Status.ProjectNotFound);
      return null;
    }

    if ('status' in projectData && projectData.status === 'Unauthorized' && isPrivate) {
      setStatus(Status.UnauthorizedUser);
      return null;
    }

    return projectData;
  }

  public async loadViewerProject() {
    const { setPageDimension, project: storeProject, setStatus } = this.viewerStore.getState();

    const project = this.projectsStore.getState().project;
    const projectData = await this.loadProjectData(storeProject.id);

    if (!projectData || !projectData.id) return;

    setStatus(Status.Loaded);
    const { widest, highest, pages } = this.processPageDimensions(projectData.pages);

    setPageDimension({
      width: widest,
      height: highest,
    });

    project.setProject({
      ...projectData,
      name: projectData.name,
      pages,
    });

    // Preload all project fonts before pages render
    this.fontManager.loadProjectFonts(projectData);

    if (!projectData.name) {
      this.listenTranslationUpdates();
    }

    return projectData;
  }

  private processPageDimensions(pages?: ProjectBody['pages']) {
    let widestDoc = 0;
    let highestDoc = 0;
    const normalizedPages: ProjectBody['pages'] = [];

    pages?.forEach(({ width, height, unit, ...rest }) => {
      if (!width || !height) return;

      const pxWidth = convertToPixel(width, unit as Unit);
      const pxHeight = convertToPixel(height, unit as Unit);

      const { height: normalizedHeight, width: normalizedWidth } =
        this.svgManager.normalizeDimension({
          originalHeight: pxHeight,
          originalWidth: pxWidth,
          expectedWidth: widestDoc,
        });

      normalizedPages.push({
        height: normalizedHeight,
        width: normalizedWidth,
        ...rest,
      });

      if (pxWidth > widestDoc) {
        widestDoc = pxWidth;
      }

      if (pxHeight > highestDoc) {
        highestDoc = pxHeight;
      }
    });

    return {
      pages: normalizedPages,
      widest: widestDoc,
      highest: highestDoc,
    };
  }

  private validateProjectId() {
    const { project, setStatus } = this.viewerStore.getState();
    const { setIsCredentialValid } = this.userStore.getState();

    if (project.id === null || project.id === '') {
      setStatus(Status.ProjectNotFound);
      setIsCredentialValid(false);
      return null;
    }

    return project.id;
  }

  private async loadDesignCollaborators({
    projectId,
    encodedId,
  }: {
    projectId: number;
    encodedId: string;
  }) {
    const { project, setCollaborators, setProject } = this.viewerStore.getState();
    const { userId } = this.userStore.getState();

    if (project.type !== 'design') return;

    try {
      const collaborators = await this.collaborateService.getMembers({
        projectId,
        encodedId,
      });

      setCollaborators(collaborators);
      const isUserOwner = collaborators.some(
        (user) => user.permissionType === 'owner' && user.permissionUserId === userId
      );

      setProject({
        isOwner: isUserOwner,
      });
    } catch (error) {
      this.errorManager.handleUnauthorizedError(error as Error);
    }
  }

  public async loadUserData(): Promise<UserData | null> {
    const { setUser, setIsCredentialValid, setIsLoading } = this.userStore.getState();
    const { setStatus } = this.viewerStore.getState();

    try {
      setIsLoading(true);
      const response = await this.userService.getUserInfo();

      const userData = response?.data as UserData;

      if (!userData || isErrorResponse(response)) {
        setStatus(Status.CredentialsInvalid);
        setIsCredentialValid(false);
        return null;
      }

      const firstName =
        userData.details.find((item) => item.type === 'first_name')?.content ??
        userData.details[0].content;
      const email = userData.email;

      setUser({
        userId: userData.id,
        details: {
          firstName,
          email,
        },
        credentials: {
          apiKey: userData.credentials.api_key,
          hash: userData.credentials.hash,
          token: userData.credentials.token,
        },
        isCredentialValid: true,
        isLoading: false,
        auth: this.userStore.getState().auth,
        userToken: this.userStore.getState().userToken,
      });

      return userData;
    } catch (error) {
      this.errorManager.handleUnauthorizedError(error as Error);
      return null;
    }
  }

  private updateProjectTitle() {
    const brandName = this.configStore.getState().brand.name;

    this.projectsStore.subscribe((state) => {
      const projectName = state.project.currentProject?.name;

      if (projectName && projectName !== document.title) {
        const isWorkspace = this.viewerStore.getState().company?.is_workspace;
        const companyName = this.viewerStore.getState().company?.name;

        document.title = isWorkspace
          ? `${companyName} | ${projectName}`
          : `${brandName} | ${projectName}`;
      }
    });
  }

  private async shouldRedirectToWebrand(projectId: string): Promise<boolean> {
    const { feature, api } = this.configStore.getState();
    const isTopLevelWindow = window.self === window.top;
    const shouldRedirect = feature.redirectToWebrand && api.baseUrl && isTopLevelWindow;

    if (!shouldRedirect) {
      return false;
    }

    const { project } = this.viewerStore.getState();
    let baseUrl = api.baseUrl;

    if (!baseUrl || typeof baseUrl !== 'string') {
      return false;
    }

    try {
      const projectsFacade = appContainer.get<ProjectsFacade>(PROJECTS_STORES_TYPES.ProjectsFacade);
      const publicProjectData = await projectsFacade.loadPublicProject(projectId, project.type);
      const workspaceDomain = publicProjectData.company.domain;

      // Replace domain with workspace subdomain if it's not the main domain
      if (workspaceDomain && workspaceDomain !== 'desygner') {
        try {
          const url = new URL(baseUrl);

          url.hostname = `${workspaceDomain}.${url.hostname}`;
          baseUrl = url.origin;
        } catch (urlError) {
          console.error('Failed to construct redirect URL:', urlError);
        }
      }
    } catch (error) {
      console.error('Failed to load project data, redirecting to webrand anyway:', error);
    }

    const searchParams = new URLSearchParams(window.location.search);
    const sourceParam = searchParams.get('source');
    const redirectPath = `/preview/${project.type}/${projectId}${
      sourceParam ? `?source=${sourceParam}` : ''
    }#pdfViewer`;

    const normalizedBaseUrl = removeTrailingSlash(baseUrl);
    const redirectUrl = `${normalizedBaseUrl}${redirectPath}`;

    window.location.href = redirectUrl;
    return true;
  }

  private async loadAureliaLoaderConfig() {
    const { api, environment } = this.configStore.getState();
    const aureliaLoaderConfig = createAureliaLoaderConfigModule({
      baseUrl: api.baseUrl,
      useCdnUrl: environment === 'prod',
      environment,
    });

    appContainer.load(aureliaLoaderConfig);
  }

  initialize = async () => {
    try {
      const projectId = this.validateProjectId();

      if (projectId === null) return;

      const { feature } = this.configStore.getState();

      if (feature.legacyShareModal) {
        await this.loadAureliaLoaderConfig();
      }

      this.keyboardShortcutsManager.init();
      await this.credentialsManager.init();
      this.configureApiGateway();
      this.listenApiGatewayUpdates();

      const shouldRedirect = await this.shouldRedirectToWebrand(projectId);

      if (shouldRedirect) {
        return;
      }

      this.loadSVGSprite();

      await this.loadViewerProject();
    } catch (error: any) {
      if (error.message.includes('not found')) {
        this.errorManager.handleNotFoundError(error);
        return;
      }

      this.errorManager.handleUnknownError(error);
    }
  };
}
